import { Message } from '../models/Message';

/**
 * Timeout threshold for message processing (45 minutes in milliseconds)
 */
export const MESSAGE_TIMEOUT_MS = 45 * 60 * 1000;

/**
 * Check if a user message has timed out (loading for 45+ minutes)
 * @param message - The message to check
 * @param nowTs - Current timestamp (defaults to Date.now())
 * @returns true if the message has timed out
 */
export const isMessageTimedOut = (message: Message, nowTs: number = Date.now()): boolean => {
  if (message.sender !== 'user' || !message.loading) return false;
  const created = Date.parse(message.createdAt || '');
  if (Number.isNaN(created)) return false;
  return nowTs - created >= MESSAGE_TIMEOUT_MS;
};

/**
 * Check if a message should show an error (either actual error or timeout)
 * @param message - The message to check
 * @param nowTs - Current timestamp (defaults to Date.now())
 * @returns true if the message has an error or has timed out
 */
export const hasMessageError = (message: Message, nowTs: number = Date.now()): boolean => {
  return message.error === true || isMessageTimedOut(message, nowTs);
};

/**
 * Check if the first message in a conversation has failed
 * (first user message has error or timeout)
 * @param messages - Array of messages in the conversation
 * @param nowTs - Current timestamp (defaults to Date.now())
 * @returns true if first user message has failed (error or timeout)
 */
export const hasFirstMessageFailed = (messages: Message[], nowTs: number = Date.now()): boolean => {
  // Find the first user message
  const firstUserMessage = messages.find(m => m.sender === 'user');

  // If no user message exists, first message hasn't failed
  if (!firstUserMessage) return false;

  // Check if first user message has error or timeout
  return hasMessageError(firstUserMessage, nowTs);
};

/**
 * Check if the critical first message has failed (the prompt-graph message that creates RAG embeddings)
 * If it fails, the conversation lacks essential context and is unusable
 * @param messages - Array of messages in the conversation
 * @param nowTs - Current timestamp (defaults to Date.now())
 * @returns true if the first user message has failed
 */
export const hasCriticalMessageFailed = (
  messages: Message[],
  nowTs: number = Date.now()
): boolean => {
  const firstUserMessage = messages.find(m => m.sender === 'user');
  if (firstUserMessage && hasMessageError(firstUserMessage, nowTs)) {
    return true;
  }
  return false;
};
